import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";

import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";

interface Props {
    /**
     * A string that will be displayed as the status text.
     */
    message?: string;

    /**
     * A boolean indicating whether there is an error associated with the
     * action bubble's action.
     */
    isError?: boolean;

    /**
     * A boolean indicating whether the action button's action did succeed.
     */
    didSucceed?: boolean;

    /**
     * An optional style override. Mostly used to override positioning of the
     * component in its rendered context.
     */
    style?: StyleType;
}

/**
 * Renders a status label below the MessageBubble component to communicate the
 * current state of the message (sending, error, and high traffic)
 */
export const ActionBubbleActionStatus = (props: Props): React.ReactElement => {
    const {message, isError, didSucceed, style} = props;

    if (didSucceed) {
        return (
            <View style={[styles.neutral, style]}>
                <LabelXSmall>{message}</LabelXSmall>
            </View>
        );
    }

    if (isError) {
        return (
            <View style={[styles.error, style]}>
                <PhosphorIcon size="small" icon={warningCircleIcon} />
                <Strut size={spacing.xxxSmall_4} />
                <LabelXSmall role="alert">{message}</LabelXSmall>
            </View>
        );
    }

    return <></>;
};

const styles = StyleSheet.create({
    neutral: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    error: {
        color: semanticColor.core.foreground.critical.default,
    },
});
